import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Upload, X, AlertCircle } from "lucide-react";
import { site, formOptions } from "@/config/site";
import { submitEstimate, parseError, newToken } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

const inputCls =
  "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-900 transition-[border-color,box-shadow] duration-200 placeholder:text-slate-400 focus:border-ember focus:outline-none focus:ring-2 focus:ring-ember/25";

function Field({ label, id, required, error, hint, children }) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm font-semibold text-slate-800">
        {label} {required && <span className="text-ember" aria-hidden="true">*</span>}
      </label>
      {children}
      {hint && !error && <p className="mt-1.5 text-xs text-slate-500">{hint}</p>}
      {error && (
        <p className="mt-1.5 flex items-center gap-1 text-sm font-medium text-red-600" id={`${id}-error`} role="alert">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" /> {error}
        </p>
      )}
    </div>
  );
}

const initial = {
  fullName: "", phone: "", email: "", address: "", propertyType: "", service: "",
  interiorExterior: "", projectSize: "", rooms: "", surfaceCondition: "", startDate: "",
  contactMethod: "", budget: "", details: "",
};

export function EstimateForm() {
  const navigate = useNavigate();
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [sending, setSending] = useState(false);
  const [files, setFiles] = useState([]);
  const [token, setToken] = useState(() => newToken());
  const errorSummaryRef = useRef(null);

  useEffect(() => {
    if (!site.turnstileSiteKey) return;
    const s = document.createElement("script");
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
    s.async = true;
    s.defer = true;
    document.head.appendChild(s);
  }, []);

  const set = (key) => (e) => {
    setValues((v) => ({ ...v, [key]: e.target.value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const onFiles = (e) => {
    const list = Array.from(e.target.files || []);
    const allowed = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];
    const bad = list.find((f) => !allowed.includes(f.type) && !/\.(jpe?g|png|webp|heic)$/i.test(f.name));
    if (bad) {
      setErrors((er) => ({ ...er, photos: "Only JPG, PNG, WEBP or HEIC photos are allowed." }));
      return;
    }
    const big = list.find((f) => f.size > 8 * 1024 * 1024);
    if (big) {
      setErrors((er) => ({ ...er, photos: "Each photo must be 8 MB or smaller." }));
      return;
    }
    if (list.length > 5) {
      setErrors((er) => ({ ...er, photos: "Please attach at most 5 photos." }));
      return;
    }
    setErrors((er) => ({ ...er, photos: undefined }));
    setFiles(list);
  };

  const validate = () => {
    const er = {};
    if (values.fullName.trim().length < 2) er.fullName = "Please enter your full name.";
    if (!/^[+0-9()\-.\s]{7,22}$/.test(values.phone.trim())) er.phone = "Please enter a valid phone number.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(values.email.trim())) er.email = "Please enter a valid email address.";
    if (values.address.trim().length < 3) er.address = "Please enter the property address or postal code.";
    if (!values.propertyType) er.propertyType = "Please select a property type.";
    if (!values.service) er.service = "Please select a service.";
    if (!values.interiorExterior) er.interiorExterior = "Please select interior or exterior.";
    if (!values.projectSize) er.projectSize = "Please select an approximate project size.";
    if (!values.surfaceCondition) er.surfaceCondition = "Please select the surface condition.";
    if (!values.startDate) er.startDate = "Please select a desired start date.";
    if (!values.contactMethod) er.contactMethod = "Please select a preferred contact method.";
    return er;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    const er = validate();
    if (Object.keys(er).length) {
      setErrors(er);
      setFormError("Please review the highlighted fields below.");
      requestAnimationFrame(() => errorSummaryRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }));
      return;
    }
    setSending(true);
    setFormError("");
    const turnstileToken =
      document.querySelector('[name="cf-turnstile-response"]')?.value || "";
    try {
      const res = await submitEstimate({ ...values, idempotencyToken: token, turnstileToken }, files);
      if (res.ok) {
        trackEvent("estimate_submit_success", { service: values.service });
        toast.success("Your estimate request has been sent.");
        setToken(newToken());
        navigate("/thank-you", { state: { type: "estimate" } });
        return;
      }
      throw new Error("Unexpected response");
    } catch (err) {
      const parsed = parseError(err);
      setErrors(parsed.fieldErrors);
      setFormError(parsed.form || "We couldn't send your request. Please try again.");
      requestAnimationFrame(() => errorSummaryRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }));
    } finally {
      setSending(false);
    }
  };

  const renderSelect = (key, label, options, required = true, hint) => (
    <Field label={label} id={`est-${key}`} required={required} error={errors[key]} hint={hint}>
      <select
        id={`est-${key}`}
        data-testid={`estimate-${key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}`}
        value={values[key]}
        onChange={set(key)}
        aria-invalid={!!errors[key]}
        aria-describedby={errors[key] ? `est-${key}-error` : undefined}
        className={inputCls}
      >
        <option value="">Select…</option>
        {options.map(([v, l]) => (
          <option key={v} value={v}>{l}</option>
        ))}
      </select>
    </Field>
  );

  return (
    <form onSubmit={onSubmit} noValidate data-testid="estimate-form" className="space-y-8">
      {formError && (
        <div ref={errorSummaryRef} role="alert" data-testid="estimate-form-error" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <p className="font-semibold">{formError}</p>
          <p className="mt-1">
            You can also reach us directly at{" "}
            <a href={site.phoneHref} className="font-semibold underline">{site.phone}</a> or{" "}
            <a href={site.emailHref} className="font-semibold underline break-all">{site.email}</a>.
          </p>
        </div>
      )}

      {/* Honeypot — hidden from humans, filled by bots */}
      <div className="absolute left-[-9999px] top-[-9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
        <label htmlFor="est-company">Company</label>
        <input id="est-company" name="company" type="text" tabIndex={-1} autoComplete="off" />
      </div>

      <fieldset>
        <legend className="font-display text-xl font-semibold text-slate-900">About you</legend>
        <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2">
          <Field label="Full name" id="est-fullName" required error={errors.fullName}>
            <input id="est-fullName" data-testid="estimate-full-name" type="text" autoComplete="name" value={values.fullName} onChange={set("fullName")} aria-invalid={!!errors.fullName} aria-describedby={errors.fullName ? "est-fullName-error" : undefined} className={inputCls} placeholder="Your name" />
          </Field>
          <Field label="Phone" id="est-phone" required error={errors.phone}>
            <input id="est-phone" data-testid="estimate-phone" type="tel" autoComplete="tel" inputMode="tel" value={values.phone} onChange={set("phone")} aria-invalid={!!errors.phone} aria-describedby={errors.phone ? "est-phone-error" : undefined} className={inputCls} placeholder="(778) 555-0123" />
          </Field>
          <Field label="Email" id="est-email" required error={errors.email}>
            <input id="est-email" data-testid="estimate-email" type="email" autoComplete="email" inputMode="email" value={values.email} onChange={set("email")} aria-invalid={!!errors.email} aria-describedby={errors.email ? "est-email-error" : undefined} className={inputCls} placeholder="you@example.com" />
          </Field>
          {renderSelect("contactMethod", "Preferred contact method", formOptions.contactMethods)}
        </div>
      </fieldset>

      <fieldset>
        <legend className="font-display text-xl font-semibold text-slate-900">About the project</legend>
        <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2">
          <Field label="Property address or postal code" id="est-address" required error={errors.address}>
            <input id="est-address" data-testid="estimate-address" type="text" autoComplete="street-address" value={values.address} onChange={set("address")} aria-invalid={!!errors.address} aria-describedby={errors.address ? "est-address-error" : undefined} className={inputCls} placeholder="e.g. 7938 110 St, Delta" />
          </Field>
          {renderSelect("propertyType", "Property type", formOptions.propertyTypes)}
          {renderSelect("service", "Painting service required", formOptions.services)}
          {renderSelect("interiorExterior", "Interior or exterior", formOptions.interiorExterior)}
          {renderSelect("projectSize", "Approximate project size", formOptions.projectSizes)}
          <Field label="Number of rooms" id="est-rooms" required={false} error={errors.rooms} hint="If relevant — e.g. 3 bedrooms and a hallway.">
            <input id="est-rooms" data-testid="estimate-rooms" type="text" inputMode="numeric" value={values.rooms} onChange={set("rooms")} className={inputCls} placeholder="Optional" />
          </Field>
          {renderSelect("surfaceCondition", "Current surface condition", formOptions.surfaceConditions)}
          {renderSelect("startDate", "Desired start date", formOptions.startDates)}
          {renderSelect("budget", "Optional budget range", formOptions.budgets, false)}
        </div>
        <div className="mt-5">
          <Field label="Additional project details" id="est-details" required={false} error={errors.details} hint="Anything else that helps us understand your project — colours you're considering, repairs needed, timelines.">
            <textarea id="est-details" data-testid="estimate-details" rows={5} value={values.details} onChange={set("details")} aria-invalid={!!errors.details} aria-describedby={errors.details ? "est-details-error" : undefined} className={inputCls} placeholder="Tell us about your project…" />
          </Field>
        </div>
        <div className="mt-5">
          <Field label="Photos (optional)" id="est-photos" required={false} error={errors.photos} hint="Up to 5 photos — JPG, PNG, WEBP or HEIC, max 8 MB each. Photos of the space help us prepare.">
            <label
              htmlFor="est-photos"
              className="flex min-h-[88px] cursor-pointer items-center justify-center gap-3 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500 transition-[border-color,background-color] duration-200 hover:border-ember hover:bg-amber-50/40"
            >
              <Upload className="h-5 w-5 text-ember" aria-hidden="true" />
              Tap to add photos of your space
            </label>
            <input id="est-photos" data-testid="estimate-photos" type="file" accept=".jpg,.jpeg,.png,.webp,.heic" multiple onChange={onFiles} className="sr-only" />
          </Field>
          {files.length > 0 && (
            <ul className="mt-3 space-y-2" data-testid="estimate-photos-list">
              {files.map((f, i) => (
                <li key={i} className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600">
                  <span className="truncate">{f.name}</span>
                  <button
                    type="button"
                    data-testid={`estimate-photo-remove-${i}`}
                    aria-label={`Remove ${f.name}`}
                    onClick={() => setFiles((fl) => fl.filter((_, j) => j !== i))}
                    className="ml-3 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-400 transition-colors duration-200 hover:bg-red-50 hover:text-red-600"
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </fieldset>

      {site.turnstileSiteKey && <div className="cf-turnstile" data-sitekey={site.turnstileSiteKey} data-testid="turnstile-widget" />}

      <p className="text-xs leading-relaxed text-slate-500">
        By submitting, you agree that North Ember Painting may contact you about your request using the details
        provided. Your information is used only to respond to your enquiry — see our{" "}
        <Link to="/privacy" className="font-medium text-ember-dark underline">Privacy Policy</Link>.
      </p>

      <button
        type="submit"
        disabled={sending}
        data-testid="estimate-submit-btn"
        className="inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-full bg-ember px-8 py-3.5 text-base font-semibold text-white shadow-lg shadow-orange-600/25 transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-ember-dark hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 sm:w-auto"
      >
        {sending && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />}
        {sending ? "Sending your request…" : "Request My Free Estimate"}
      </button>
    </form>
  );
}
